import cx from "classnames";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import { ICDCodingBlock } from "components/ICDCoding";
import type { ICDUpdateData } from "components/ICDCoding/icdAdapter";
import { ControlledTextArea } from "components/inputs/TextArea/ControlledTextArea";
import { useCallback, useEffect } from "react";
import type { Path } from "react-hook-form";
import { useForm } from "react-hook-form";
import { usePatchServiceDateOfInjuryByIdMutation } from "services/doi-api";
import type { DocumentManagementDataType } from "types/DataTypes";
import { useSelector } from "utils/redux";

type Props = {
  onSave: (field: string | null, value: unknown) => Promise<void>;
  service: DocumentManagementDataType;
};

type FormValues = {
  "service_date_of_injury.accepted_conditions": string;
  "service_date_of_injury.denied_conditions": string;
  "service_date_of_injury.accident_description": string;
  general_notes: string;
  accepted_conditions_notes: string;
  denied_conditions_notes: string;
  medical_legal_notes: string;
  potential_issues_notes: string;
  medication_notes: string;
  therapy_notes: string;
};

/*
we have two different places for data to go

ServiceDateOfInjury
  accident_description: string | null;
  accepted_conditions: string | null;
  denied_conditions: string | null;

DocumentManagementFile
  general_notes: string;
  accepted_conditions_notes: string;
  denied_conditions_notes: string;
  medical_legal_notes: string;
  potential_issues_notes: string;
  medication_notes: string;

  are associated with the service_date_of_injury and will change when the sDOI toggle in the header pill is changed
*/
const DMClinicianNotes = ({ service, onSave }: Props) => {
  const showClinicianNotes = useSelector(
    (state) => state.drawer.showClinicianNotes,
  );

  const { selectedServiceDoi, isLoading } = useServiceDOI(service);
  const [patchServiceDateOfInjury] = usePatchServiceDateOfInjuryByIdMutation();

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      "service_date_of_injury.accepted_conditions":
        selectedServiceDoi?.accepted_conditions || "",
      "service_date_of_injury.denied_conditions":
        selectedServiceDoi?.denied_conditions || "",
      "service_date_of_injury.accident_description":
        selectedServiceDoi?.accident_description || "",
      general_notes: service.general_notes || "",
      accepted_conditions_notes: service.accepted_conditions_notes || "",
      denied_conditions_notes: service.denied_conditions_notes || "",
      medical_legal_notes: service.medical_legal_notes || "",
      potential_issues_notes: service.potential_issues_notes || "",
      medication_notes: service.medication_notes || "",
      therapy_notes: service.therapy_notes || "",
    },
  });

  // update form values when selected DOI changes
  useEffect(() => {
    if (selectedServiceDoi) {
      const doiFields = {
        "service_date_of_injury.accepted_conditions":
          selectedServiceDoi.accepted_conditions,
        "service_date_of_injury.denied_conditions":
          selectedServiceDoi.denied_conditions,
        "service_date_of_injury.accident_description":
          selectedServiceDoi.accident_description,
      } as const;

      Object.entries(doiFields).forEach(([field, value]) => {
        setValue(field as Path<FormValues>, value || "");
      });
    }
  }, [selectedServiceDoi, setValue]);

  const handleFieldSave = useCallback(
    async (field: keyof FormValues, value: string) => {
      await onSave(field, value);
    },
    [onSave],
  );

  // handler for saving ICD data from ICDCodingBlock directly to the ServiceDateOfInjury
  const handleSaveIcdData = useCallback(
    async (data: ICDUpdateData) => {
      if (selectedServiceDoi?.id) {
        try {
          await patchServiceDateOfInjury({
            id: selectedServiceDoi.id.toString(),
            data,
          });
        } catch (error) {
          console.error(
            "Failed to update ServiceDateOfInjury ICD data:",
            error,
          );
        }
      }
    },
    [selectedServiceDoi?.id, patchServiceDateOfInjury],
  );

  return (
    <div
      className={cx("documents notes-msa flex jc-se", {
        open: showClinicianNotes,
      })}
    >
      <div className="documents-wrapper notes">
        <div className="swim-wrapper h-100">
          <div className="grid-layout no-gap-tb swimlanes msa-notes">
            <div className="swim-headers">Clinician Notes</div>

            <div className="swim-column">
              <ICDCodingBlock
                data={selectedServiceDoi || undefined}
                isLoading={isLoading}
                onSave={handleSaveIcdData}
              />
              <div className="data-block">
                <div className="data-block-header">Allocation Notes</div>

                <div className="grid-layout gl-1 notes-grid">
                  <ControlledTextArea
                    control={control}
                    name="service_date_of_injury.accepted_conditions"
                    label="Accepted Conditions"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "service_date_of_injury.accepted_conditions",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="service_date_of_injury.denied_conditions"
                    label="Denied Conditions"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "service_date_of_injury.denied_conditions",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="accepted_conditions_notes"
                    label="Accepted Conditions Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "accepted_conditions_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="denied_conditions_notes"
                    label="Denied Conditions Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "denied_conditions_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="general_notes"
                    label="Treatment Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("general_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="potential_issues_notes"
                    label="Potential development issues/missing materials/ confirm MPH reviewed with run date"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "potential_issues_notes",
                        value.toString(),
                      )
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="medication_notes"
                    label="Medication Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("medication_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="therapy_notes"
                    label="Therapy Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("therapy_notes", value.toString())
                    }
                  />
                  <ControlledTextArea
                    control={control}
                    name="medical_legal_notes"
                    label="Medical Legal Notes"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave("medical_legal_notes", value.toString())
                    }
                  />
                </div>
              </div>

              <div className="data-block">
                <div className="data-block-header">Accident</div>

                <div className="grid-layout gl-1 notes-grid">
                  <ControlledTextArea
                    control={control}
                    name="service_date_of_injury.accident_description"
                    label="Accident Description"
                    errors={errors}
                    advancedEdit={true}
                    onSave={(value) =>
                      handleFieldSave(
                        "service_date_of_injury.accident_description",
                        value.toString(),
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DMClinicianNotes;
